import { useContext, useState, useEffect, useMemo } from "react";
import { LocaleContext } from "../../contexts/LocaleContext";
import Pagination from "../../components/Pagination";
import { securityService } from "../../services/securityService";
import Loading from "../../components/common/Loading";
import { params } from "../../config/params";
import { searchParams } from "../../config/searchParams";

const emptyFilters = {
  statusCode: "",
  ipAddress: "",
  dateFrom: "",
  dateTo: "",
};

const AuthFailures = () => {
  const { t } = useContext(LocaleContext);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(emptyFilters);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setLoading(true);
    getAllAsync();
  }, []);

  const buildParams = (extraFilters = filters) => {
    const merged = { ...params };
    if (extraFilters.statusCode) merged.statusCode = extraFilters.statusCode;
    if (extraFilters.ipAddress) merged.ipAddress = extraFilters.ipAddress;
    if (extraFilters.dateFrom) merged.dateFrom = extraFilters.dateFrom;
    if (extraFilters.dateTo) merged.dateTo = extraFilters.dateTo;
    return merged;
  };

  const getAllAsync = async (extraFilters = filters) => {
    try {
      const result = await securityService.getAuthFailuresAsync(buildParams(extraFilters));
      setData(result);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getAllSearchAsync = async (signal) => {
    try {
      const result = await securityService.getAuthFailuresAsync(
        { ...searchParams, ...buildParams() },
        signal
      );
      setData(result);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (search.trim().length < 2) {
      params.pageNumber = 1;
      getAllAsync();
      return;
    }
    const controller = new AbortController();
    const delay = setTimeout(() => {
      searchParams.search = search.trim();
      searchParams.pageNumber = 1;
      getAllSearchAsync(controller.signal);
    }, 400);
    return () => {
      controller.abort();
      clearTimeout(delay);
    };
  }, [search]);

  const onPageChange = (pageNumber) => {
    setLoading(true);
    if (!search) {
      params.pageNumber = pageNumber;
      getAllAsync();
      return;
    }
    searchParams.pageNumber = pageNumber;
    getAllSearchAsync();
  };

  const applyFilters = () => {
    params.pageNumber = 1;
    setLoading(true);
    setShowFilters(false);
    getAllAsync(filters);
  };

  const clearFilters = () => {
    setFilters(emptyFilters);
    params.pageNumber = 1;
    setLoading(true);
    getAllAsync(emptyFilters);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const topOffendingIps = useMemo(() => {
    const items = data?.items || [];
    const counts = {};
    items.forEach((item) => {
      if (!item.ipAddress) return;
      counts[item.ipAddress] = (counts[item.ipAddress] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [data]);

  return (
    <>
      {loading && <Loading />}
      <section className="section-auth-failures">
        <div className="default-heading">{t("authFailureMonitoring")}</div>

        <section className="section-dashboard-cards mb-3">
          <div className="row">
            <div className="col-12 col-md-6">
              <div className="d-card d-card-primary">
                <div className="d-card-header">{t("topOffendingIps")}</div>
                <div className="d-card-body">
                  {topOffendingIps.length === 0 ? (
                    <div className="d-card-body-1">
                      <span>{t("noResultsFound")}</span>
                    </div>
                  ) : (
                    <ul className="list-unstyled mb-0 w-100">
                      {topOffendingIps.map(([ip, count], idx) => (
                        <li
                          key={ip}
                          className="d-flex justify-content-between align-items-center py-1"
                        >
                          <span>
                            {idx === 0 && (
                              <i className="fas fa-fire text-danger me-2"></i>
                            )}
                            {ip}
                          </span>
                          <span className="badge bg-danger">
                            {count} {t("failures")}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section-grid">
          <div className="grid-card">
            <div className="grid-card-header">
              <strong>{t("authFailureMonitoring")}</strong>
              <p>{t("viewAndMonitorAuthenticationFailures")}</p>
            </div>
            <div className="grid-card-body">
              <div className="table-header">
                <div className="table-header-icons hidden">
                  <div className="icon-box"><i className="fas fa-file-pdf"></i></div>
                  <div className="icon-box"><i className="fas fa-file-excel"></i></div>
                  <div className="icon-box"><i className="fas fa-print"></i></div>
                </div>
                <div className="table-search">
                  <div className="input-icon input-icon-width">
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="form-control input-search"
                      placeholder={t("search")}
                    />
                    <i className="fas fa-search"></i>
                  </div>
                </div>
              </div>
              <div className="table-delete-option mt-3">
                <b>{t("allAuthFailures")}</b>
                <div className="d-flex align-items-center gap-2">
                  <i
                    className="fas fa-filter cursor-pointer"
                    onClick={() => setShowFilters((v) => !v)}
                  />
                </div>
              </div>
              {showFilters && (
                <div className="p-3 border rounded mb-3 bg-white">
                  <div className="row g-2">
                    <div className="col-12 col-md-3">
                      <label className="form-label fw-bold">{t("statusCode")}</label>
                      <select
                        className="form-control form-control-sm"
                        value={filters.statusCode}
                        onChange={(e) => handleFilterChange("statusCode", e.target.value)}
                      >
                        <option value="">{t("all")}</option>
                        <option value="401">401</option>
                        <option value="403">403</option>
                      </select>
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label fw-bold">{t("ipAddress")}</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={filters.ipAddress}
                        onChange={(e) => handleFilterChange("ipAddress", e.target.value)}
                        placeholder={t("ipAddress")}
                      />
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label fw-bold">{t("date")}</label>
                      <input
                        type="date"
                        className="form-control form-control-sm mb-1"
                        value={filters.dateFrom}
                        onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
                      />
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={filters.dateTo}
                        onChange={(e) => handleFilterChange("dateTo", e.target.value)}
                      />
                    </div>
                    <div className="col-12 d-flex gap-2 justify-content-end">
                      <button className="btn-default" onClick={applyFilters}>
                        {t("search")}
                      </button>
                      <button className="btn-secondary" onClick={clearFilters}>
                        {t("cancel")}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <div className="my-table">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t("timestamp")}</th>
                      <th>{t("endpoint")}</th>
                      <th>{t("statusCode")}</th>
                      <th>{t("failureReason")}</th>
                      <th>{t("ipAddress")}</th>
                      <th>{t("location")}</th>
                      <th>{t("deviceType")}</th>
                      <th>{t("userName")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items?.map((item) => (
                      <tr key={item.id}>
                        <td>{item.occurredAt?.toDate?.() ?? item.occurredAt}</td>
                        <td>
                          <span className="badge bg-secondary me-1">{item.httpMethod}</span>
                          {item.endpoint}
                        </td>
                        <td>
                          <span
                            className={`badge ${item.statusCode === 401 ? "bg-warning" : "bg-danger"}`}
                          >
                            {item.statusCode}
                          </span>
                        </td>
                        <td>{item.failureReason}</td>
                        <td>{item.ipAddress}</td>
                        <td>
                          {[item.city, item.countryCode].filter(Boolean).join(", ") || "-"}
                        </td>
                        <td>{item.deviceType || "-"}</td>
                        <td>{item.userName || "-"}</td>
                      </tr>
                    ))}
                    {data?.items?.length === 0 && (
                      <tr>
                        <td colSpan="100%" className="text-center py-3">
                          {t("noResultsFound")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="grid-footer">
              {data && (
                <Pagination
                  t={t}
                  pageNumber={data.pageNumber}
                  totalPages={data.totalPages}
                  hasNextPage={data.hasNextPage}
                  hasPreviousPage={data.hasPreviousPage}
                  onPageChange={onPageChange}
                />
              )}
            </div>
          </div>
        </section>
      </section>
    </>
  );
};

export default AuthFailures;
